/**
 * Date formatting utilities for consistent display of full and partial dates.
 *
 * Partial date formats:
 * - YYYY → displayed as "YYYY"
 * - YYYY-MM → displayed as "MM.YYYY"
 * - YYYY-MM-DD → displayed as "DD.MM.YYYY"
 * - DD.MM.YYYY → displayed as "DD.MM.YYYY"
 *
 * Timezone convention: all dates are stored in UTC (ISO 8601) on the backend
 * and displayed in the user's local timezone on the frontend. This is the
 * standard behaviour of JavaScript's Date constructor — no additional
 * conversion is applied.
 */

/**
 * Format a date string (ISO or German format) as DD.MM.YYYY, MM.YYYY, or YYYY.
 * Returns the original string if it cannot be parsed.
 */
export function formatDate(value: string | Date): string {
  if (value instanceof Date) {
    const d = String(value.getDate()).padStart(2, '0');
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const y = value.getFullYear();
    return `${d}.${m}.${y}`;
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  // YYYY-MM-DD → DD.MM.YYYY
  const fullDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (fullDateMatch) {
    return `${fullDateMatch[3]}.${fullDateMatch[2]}.${fullDateMatch[1]}`;
  }

  // YYYY-MM → MM.YYYY
  const yearMonthMatch = value.match(/^(\d{4})-(\d{2})$/);
  if (yearMonthMatch) {
    return `${yearMonthMatch[2]}.${yearMonthMatch[1]}`;
  }

  // YYYY → YYYY
  const yearMatch = value.match(/^(\d{4})$/);
  if (yearMatch) {
    return value;
  }

  // Already in DD.MM.YYYY or MM.YYYY format
  return value;
}

/**
 * Parse a user input date string (DD.MM.YYYY or partial) to ISO format.
 * Returns null if invalid.
 */
export function parseDate(value: string): string | null {
  if (!value || !value.trim()) {
    return null;
  }

  const trimmed = value.trim();

  // DD.MM.YYYY → YYYY-MM-DD
  const fullDateMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (fullDateMatch) {
    const day = fullDateMatch[1].padStart(2, '0');
    const month = fullDateMatch[2].padStart(2, '0');
    const year = fullDateMatch[3];
    return `${year}-${month}-${day}`;
  }

  // MM.YYYY → YYYY-MM
  const yearMonthMatch = trimmed.match(/^(\d{1,2})\.(\d{4})$/);
  if (yearMonthMatch) {
    const month = yearMonthMatch[1].padStart(2, '0');
    const year = yearMonthMatch[2];
    return `${year}-${month}`;
  }

  // YYYY → YYYY
  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    return trimmed;
  }

  // Already in ISO format
  if (trimmed.match(/^\d{4}(-\d{2}(-\d{2})?)?$/)) {
    return trimmed;
  }

  return null;
}

/**
 * Format a Date object or ISO timestamp as DD.MM.YYYY, HH:MM.
 * Returns the original string if it cannot be parsed.
 */
export function formatDateTime(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year}, ${hours}:${mins}`;
}

/**
 * Format a Date object as HH:MM.
 */
export function formatTime(value: Date): string {
  const hours = String(value.getHours()).padStart(2, '0');
  const mins = String(value.getMinutes()).padStart(2, '0');
  return `${hours}:${mins}`;
}
